import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getAuthToken } from '../lib/authToken';

interface NotificationContextType {
  unreadCount: number;
  setUnreadCount: (count: number) => void;
  refreshUnreadCount: () => Promise<void>;
  decrementUnreadCount: () => void;
  incrementUnreadCount: () => void;
  markAsRead: () => void;
  markAsUnread: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

async function fetchUnreadCount(): Promise<number> {
  const token = getAuthToken();
  if (!token) return 0;
  const response = await fetch('/api/notifications/unread-count', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return 0;
  const json = await response.json();
  return Number(json?.data?.unreadCount) || 0;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAuthenticated } = useAuth();

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    try {
      const count = await fetchUnreadCount();
      setUnreadCount(count);
    } catch {
      /* keep previous */
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshUnreadCount();
    if (!isAuthenticated) return undefined;
    const timer = window.setInterval(refreshUnreadCount, 60000);
    return () => window.clearInterval(timer);
  }, [isAuthenticated, refreshUnreadCount]);

  const decrementUnreadCount = () => {
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const incrementUnreadCount = () => {
    setUnreadCount((prev) => prev + 1);
  };

  const markAsRead = () => {
    decrementUnreadCount();
  };

  const markAsUnread = () => {
    incrementUnreadCount();
  };

  const value: NotificationContextType = {
    unreadCount,
    setUnreadCount,
    refreshUnreadCount,
    decrementUnreadCount,
    incrementUnreadCount,
    markAsRead,
    markAsUnread,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
